import { useSheetManager } from '@/lib/ui';
import { useBrowserNavigation } from '@/lib/ui/BrowserNavigationContext';
import type { SheetNavigationList, SheetNavigationItem } from '@/lib/ui/SheetManager';
import EntityDetailView from '@/components/EntityDetailView';

interface ShowEntityDetailOptions {
  keepOpenOnRouteChange?: boolean;
  title?: string;
  /** Navigation list for prev/next browsing through entities. When omitted, falls back to browser context. */
  navigationList?: SheetNavigationList;
}

export function useEntityDetailSheet() {
  const { openSheet } = useSheetManager();
  const { entityNavigationList } = useBrowserNavigation();

  const showEntityDetail = (
    entityId: number,
    entityType: string,
    options?: ShowEntityDetailOptions
  ) => {
    const navList = options?.navigationList ?? entityNavigationList;
    openSheet({
      id: `entity-detail-${entityId}`,
      title: options?.title ?? 'Entity Details',
      content: <EntityDetailView id={entityId.toString()} entityType={entityType} />,
      maxWidth: 'sm:max-w-4xl',
      keepOpenOnRouteChange: options?.keepOpenOnRouteChange ?? false,
      navigationList: navList,
      currentNavigationId: navList ? String(entityId) : undefined,
    });
  };

  return { showEntityDetail };
}

/** Helper to build a navigation list for entities */
export function buildEntityNavigationList(
  entities: Array<{ id: number; name_1?: string; type?: string; entityType?: string }>,
  entityType: string
): SheetNavigationList {
  return {
    items: entities.map((e) => ({
      id: String(e.id),
      title: e.name_1 || 'Entity Details',
    })),
    renderContent: (item: SheetNavigationItem) => {
      const entity = entities.find((e) => String(e.id) === item.id);
      const type = entity?.entityType || entity?.type || entityType;
      return <EntityDetailView id={item.id} entityType={type} />;
    },
    maxWidth: 'sm:max-w-4xl',
  };
}
